/**
 * The Order Queue's density tier from the measured list width.
 *
 * Like useContainerWidth (one ResizeObserver on the list's scroll container),
 * except React state changes only when the resolved TIER changes, never on
 * each pixel: the sidebar animation would otherwise re-render the whole
 * OrdersPage on every frame. The latest width lives in a ref; the state holds
 * the width at the last tier change, and the tier is derived from it during
 * render, so a change of options (client columns, chat, the Type toggle) is
 * correct in the same render. When an option change moves a tier boundary
 * past the stored width, a layout effect re-resolves from the latest width
 * before paint.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  resolveQueueTier,
  type QueueColumnOptions,
  type QueueDensity,
} from '../components/orders/queue-columns';

export interface QueueTier {
  density: QueueDensity;
  belowNarrow: boolean;
}

function sameTier(a: number | null, b: number | null, opts: QueueColumnOptions): boolean {
  const ta = resolveQueueTier(a, opts);
  const tb = resolveQueueTier(b, opts);
  return ta.density === tb.density && ta.belowNarrow === tb.belowNarrow;
}

export function useQueueTier<T extends HTMLElement>(
  opts: QueueColumnOptions,
): [(node: T | null) => void, QueueTier] {
  const { showClientColumns, showChat, showType } = opts;
  const [element, setElement] = useState<T | null>(null);
  // The width at the last tier change (null until measured).
  const [width, setWidth] = useState<number | null>(null);
  const latestWidthRef = useRef<number | null>(null);
  const optsRef = useRef(opts);
  optsRef.current = opts;

  const ref = useCallback((node: T | null) => setElement(node), []);

  // Mirror of `width`, compared OUTSIDE setState: a functional update that
  // returns the same value can still cost one extra render before React bails
  // out, which is exactly the per-frame render this hook exists to avoid.
  const committedWidthRef = useRef<number | null>(null);
  const commitIfTierChanged = useCallback((next: number | null) => {
    latestWidthRef.current = next;
    const prev = committedWidthRef.current;
    if (prev === next || (prev !== null && sameTier(prev, next, optsRef.current))) return;
    committedWidthRef.current = next;
    setWidth(next);
  }, []);

  useEffect(() => {
    if (!element) return;
    if (typeof ResizeObserver === 'undefined') {
      commitIfTierChanged(element.getBoundingClientRect().width);
      return;
    }
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) commitIfTierChanged(entry.contentRect.width);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [element, commitIfTierChanged]);

  // An option change can move a cut-off past the stored width while the real
  // width (in the ref) sits on the other side of it.
  useLayoutEffect(() => {
    commitIfTierChanged(latestWidthRef.current);
  }, [showClientColumns, showChat, showType, commitIfTierChanged]);

  const tier = resolveQueueTier(width, { showClientColumns, showChat, showType });
  return [ref, tier];
}
